import { type FC, type ReactNode, useCallback, useState } from 'react';

import { useRevalidator } from '@remix-run/react';
import { useQueryClient } from '@tanstack/react-query';

import { useCommunityCableSubscription } from '../_hooks/useCommunityCableSubscription';
import { invalidateCommunityFeedQueries } from '../_hooks/useCommunityFeedActionMutation';
import { CommunityDataContext, type CommunityMessageDataType } from './CommunityDataContext';

interface Props {
  isMember: boolean;
  children: ReactNode;
}

export const CommunityDataProvider: FC<Props> = ({ children, isMember }) => {
  const [messagesState, setMessagesState] = useState<CommunityMessageDataType>({
    unreadCount: undefined,
    unreadOverridesByChannelId: {},
  });
  const { revalidate } = useRevalidator();
  const queryClient = useQueryClient();

  const { perform } = useCommunityCableSubscription({
    isMember,
    eventHandlers: {
      'chat:unread_count': (event) => {
        setMessagesState((prev) => {
          const next = { ...prev };
          if (typeof event.totalUnreadChatCount === 'number') {
            next.unreadCount = event.totalUnreadChatCount;
          }
          if (typeof event.getstreamChannelId === 'string' && typeof event.unreadMessagesCount === 'number') {
            next.unreadOverridesByChannelId = {
              ...prev.unreadOverridesByChannelId,
              [event.getstreamChannelId]: event.unreadMessagesCount,
            };
          }
          return next;
        });
      },
      'member:status_changed': () => {
        revalidate();
      },
      'member:block_changed': () => {
        // A block changes which messages count as unread, so drop live per-channel
        // overrides before the refreshed token map replaces them.
        setMessagesState((prev) => ({ ...prev, unreadOverridesByChannelId: {} }));
        revalidate();
        invalidateCommunityFeedQueries(queryClient);
        queryClient.invalidateQueries({ queryKey: ['communityChatToken'] });
      },
    },
    onRejected: () => {
      revalidate();
    },
  });

  const markChatRead = useCallback(
    (getstreamChannelId: string, lastMessageAt?: string) => {
      perform('mark_read', { getstream_channel_id: getstreamChannelId, last_message_at: lastMessageAt });
    },
    [perform],
  );

  return (
    <CommunityDataContext.Provider value={{ messages: messagesState, markChatRead }}>
      {children}
    </CommunityDataContext.Provider>
  );
};
